import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faStore } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../styles/theme';

const FooterContainer = styled.footer`
  background-color: ${theme.colors.secondary};
  color: rgba(255, 255, 255, 0.8);
  padding: 3rem 0;
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  padding: 0 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;

  &:nth-child(2) {
    align-items: center;
    text-align: center;
  }

  &:last-child {
    align-items: flex-end;
    text-align: right;
  }

  @media (max-width: 480px) {
    &,
    &:nth-child(2),
    &:last-child {
      align-items: center;
      text-align: center;
    }
  }
`;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.white};
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.accent};
  }
`;

const Copyright = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem 2rem 0;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 2rem;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.9rem;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const PartnerLink = styled.a`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.accent};
  }

  img {
    height: 24px;
    width: auto;
    filter: brightness(0) invert(1);
    opacity: 0.6;
    transition: opacity ${theme.transitions.fast};
  }

  &:hover img {
    opacity: 1;
  }
`;

const SocialIconLink = styled.a`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  transition: color ${theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${theme.colors.accent};
  }

  img {
    height: 24px;
    width: auto;
  }
`;

const Footer: React.FC = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <FooterTitle>Kundeservice</FooterTitle>
          <FooterLink to="/contact">Kontakt oss</FooterLink>
          <FooterLink to="/faq">FAQs</FooterLink>
          <FooterLink to="/terms">Våre betingelser</FooterLink>
          <FooterLink to="/om-oss">Om oss</FooterLink>
        </FooterSection>

        <FooterSection>
          <FooterTitle>Følg oss</FooterTitle>
          <SocialIcons>
            <SocialIconLink
              href="https://www.facebook.com/share/1G2cejLcqV/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <FontAwesomeIcon icon={faFacebookF} />
            </SocialIconLink>
            <SocialIconLink
              href="https://instagram.com/tryllepapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <FontAwesomeIcon icon={faInstagram} />
            </SocialIconLink>
            <SocialIconLink
              href="https://tryllepapp.no"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Nettbutikk"
            >
              <FontAwesomeIcon icon={faStore} />
            </SocialIconLink>
          </SocialIcons>
        </FooterSection>

        <FooterSection>
          <FooterTitle>Våre partnere</FooterTitle>
          <SocialIcons>
            <PartnerLink
              href="https://www.tcgforeningen.no/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/tcgforeningen-logo.png" alt="TCG Foreningen" />
            </PartnerLink>
          </SocialIcons>
        </FooterSection>
      </FooterContent>

      <Copyright>
        © {new Date().getFullYear()} Tryllepapp. Alle rettigheter forbeholdt.
        <br />
        Org. nr. 936 254 845 MVA
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
